// To add new integration image:
// 1. Add the `NewIntegrationImage.svg` file inside the `integrations` folder.
// 2. Add the following import in this file: `import { ReactComponent as NewIntegrationImage } from "./NewIntegrationImage.svg";`
// 3. Add a new `NewIntegrationImage` entry to the `integrations` dictionary,
//    wrapping the new integration image with the `integrationImageWrapper`
//    function: `NewIntegrationImage: integrationImageWrapper("NewIntegrationImage", NewIntegrationImage),`

// To use integration image:
// 1. Import the component: import IntegrationImage from "../assets/img/integrations";
// 2. Call the component: <IntegrationImage.ImageName />

// To use integration image based on integration id:
// 1. Import the component: import IntegrationImage, { integrationImageById }  from "../assets/img/integrations";
// 2. Call the function for main svg: integrationImageById('integrationId',IntegrationImage)
// 3. Call the function for icon svg: integrationImageById('integrationId',IntegrationImage, true)
// 4. Call the function for special svg: integrationImageById('integrationId',IntegrationImage, false, "", provider/win32/darwin)

import React from 'react';
import classnames from 'classnames';
import styles from './integrations.module.scss';
import { specialIntegrationImageType } from 'helpers/constants';

import { ReactComponent as AirtableIcon } from './airtable_ico.svg';
import { ReactComponent as Airtable } from './airtable.svg';

import { ReactComponent as AsanaIcon } from './asana_ico.svg';
import { ReactComponent as Asana } from './asana.svg';

import { ReactComponent as BasecampIcon } from './basecamp_ico.svg';
import { ReactComponent as Basecamp } from './basecamp.svg';

import { ReactComponent as BoxIcon } from './box_ico.svg';
import { ReactComponent as Box } from './box.svg';

import { ReactComponent as ConfluenceIcon } from './confluence_ico.svg';
import { ReactComponent as Confluence } from './confluence.svg';

import { ReactComponent as DocusignIcon } from './docusign_ico.svg';
import { ReactComponent as Docusign } from './docusign.svg';

import { ReactComponent as DropboxIcon } from './dropbox_ico.svg';
import { ReactComponent as Dropbox } from './dropbox.svg';

import { ReactComponent as DropboxPaperIcon } from './dropbox_paper_ico.svg';
import { ReactComponent as DropboxPaper } from './dropbox_paper.svg';

import { ReactComponent as EvernoteIcon } from './evernote_ico.svg';
import { ReactComponent as Evernote } from './evernote.svg';

import { ReactComponent as FigmaIcon } from './figma_ico.svg';
import { ReactComponent as Figma } from './figma.svg';

import { ReactComponent as Folders } from './folders.svg';

import { ReactComponent as GithubGistIcon } from './github_gist_ico.svg';
import { ReactComponent as GithubGist } from './github_gist.svg';

import { ReactComponent as GithubIcon } from './github_ico.svg';
import { ReactComponent as Github } from './github.svg';

import { ReactComponent as GmailIcon } from './gmail_ico.svg';
import { ReactComponent as Gmail } from './gmail.svg';

import { ReactComponent as GoogleAdminContactsIcon } from './google_admin_contacts_ico.svg';
import { ReactComponent as GoogleAdminContacts } from './google_admin_contacts.svg';

import { ReactComponent as GoogleClassroomIcon } from './google_classroom_ico.svg';
import { ReactComponent as GoogleClassroom } from './google_classroom.svg';

import { ReactComponent as GoogleContactsIcon } from './google_contacts_ico.svg';
import { ReactComponent as GoogleContacts } from './google_contacts.svg';

import { ReactComponent as GoogleDriveIcon } from './google_drive_ico.svg';
import { ReactComponent as GoogleDrive } from './google_drive.svg';

import { ReactComponent as GoogleDriveProviderIcon } from './google_drive_provider_ico.svg';

import { ReactComponent as HellosignIcon } from './hellosign_ico.svg';
import { ReactComponent as Hellosign } from './hellosign.svg';

import { ReactComponent as LocalfilesIcon } from './localfiles_ico.svg';
import { ReactComponent as Localfiles } from './localfiles.svg';

import { ReactComponent as LocalfilesDarwin } from './localfiles_darwin.svg';

import { ReactComponent as LocalfilesWin32 } from './localfiles_win32.svg';

import { ReactComponent as MailchimpIcon } from './mailchimp_ico.svg';
import { ReactComponent as Mailchimp } from './mailchimp.svg';

import { ReactComponent as MicrosoftCalendarIcon } from './microsoftcalendar_ico.svg';
import { ReactComponent as MicrosoftCalendar } from './microsoftcalendar.svg';

import { ReactComponent as MiroIcon } from './miro_ico.svg';
import { ReactComponent as Miro } from './miro.svg';

import { ReactComponent as NoteJoyIcon } from './notejoy_ico.svg';
import { ReactComponent as NoteJoy } from './notejoy.svg';

import { ReactComponent as OneDriveIcon } from './onedrive_ico.svg';
import { ReactComponent as OneDrive } from './onedrive.svg';

import { ReactComponent as OneNoteIcon } from './onenote_ico.svg';
import { ReactComponent as OneNote } from './onenote.svg';

import { ReactComponent as OutlookIcon } from './outlook_ico.svg';
import { ReactComponent as Outlook } from './outlook.svg';

import { ReactComponent as PcloudIcon } from './pcloud_ico.svg';
import { ReactComponent as Pcloud } from './pcloud.svg';

import { ReactComponent as PocketIcon } from './pocket_ico.svg';
import { ReactComponent as Pocket } from './pocket.svg';

import { ReactComponent as QuipIcon } from './quip_ico.svg';
import { ReactComponent as Quip } from './quip.svg';

import { ReactComponent as RightsignatureIcon } from './rightsignature_ico.svg';
import { ReactComponent as Rightsignature } from './rightsignature.svg';

import { ReactComponent as SharepointIcon } from './sharepoint_ico.svg';
import { ReactComponent as Sharepoint } from './sharepoint.svg';

import { ReactComponent as SlackIcon } from './slack_ico.svg';
import { ReactComponent as Slack } from './slack.svg';

import { ReactComponent as SurveyMonkeyIcon } from './surveymonkey_ico.svg';
import { ReactComponent as SurveyMonkey } from './surveymonkey.svg';

import { ReactComponent as TeamsIcon } from './teams_ico.svg';
import { ReactComponent as Teams } from './teams.svg';

import { ReactComponent as TrelloIcon } from './trello_ico.svg';
import { ReactComponent as Trello } from './trello.svg';

import { ReactComponent as YammerIcon } from './yammer_ico.svg';
import { ReactComponent as Yammer } from './yammer.svg';

import { ReactComponent as ZohoDocsIcon } from './zoho_docs_ico.svg';
import { ReactComponent as ZohoDocs } from './zoho_docs.svg';

import { ReactComponent as ZoomIcon } from './zoom_ico.svg';
import { ReactComponent as Zoom } from './zoom.svg';

function integrationImageWrapper(imageName, IntegrationImage, imageClassName) {
  return ({
    className, // eslint-disable-line
    textPosition, // eslint-disable-line
    ...props
  }) => {
    const classes = classnames(
      className,
      styles[imageName],
      styles[imageClassName],
      styles[`textPosition-${textPosition}`]
    );
    return <IntegrationImage className={classes} {...props} />;
  };
}

const integrationImages = {
  AirtableIcon: integrationImageWrapper('AirtableIcon', AirtableIcon),
  Airtable: integrationImageWrapper('Airtable', Airtable),

  AsanaIcon: integrationImageWrapper('AsanaIcon', AsanaIcon),
  Asana: integrationImageWrapper('Asana', Asana),

  BasecampIcon: integrationImageWrapper('BasecampIcon', BasecampIcon),
  Basecamp: integrationImageWrapper('Basecamp', Basecamp, 'svg-grayscale'),

  BoxIcon: integrationImageWrapper('BoxIcon', BoxIcon),
  Box: integrationImageWrapper('Box', Box),

  ConfluenceIcon: integrationImageWrapper('ConfluenceIcon', ConfluenceIcon),
  Confluence: integrationImageWrapper('Confluence', Confluence),

  DocusignIcon: integrationImageWrapper('DocusignIcon', DocusignIcon),
  Docusign: integrationImageWrapper('Docusign', Docusign),

  DropboxIcon: integrationImageWrapper('DropboxIcon', DropboxIcon),
  Dropbox: integrationImageWrapper('Dropbox', Dropbox),

  DropboxPaperIcon: integrationImageWrapper('DropboxPaperIcon', DropboxPaperIcon),
  DropboxPaper: integrationImageWrapper('DropboxPaper', DropboxPaper),

  EvernoteIcon: integrationImageWrapper('EvernoteIcon', EvernoteIcon),
  Evernote: integrationImageWrapper('Evernote', Evernote),

  FigmaIcon: integrationImageWrapper('FigmaIcon', FigmaIcon),
  Figma: integrationImageWrapper('Figma', Figma, 'svg-grayscale'),

  Folders: integrationImageWrapper('Folders', Folders),

  GithubGistIcon: integrationImageWrapper('GithubGistIcon', GithubGistIcon),
  GithubGist: integrationImageWrapper('GithubGist', GithubGist),

  GithubIcon: integrationImageWrapper('GithubIcon', GithubIcon),
  Github: integrationImageWrapper('Github', Github),

  GmailIcon: integrationImageWrapper('GmailIcon', GmailIcon),
  Gmail: integrationImageWrapper('Gmail', Gmail, 'svg-grayscale'),

  GoogleAdminContactsIcon: integrationImageWrapper(
    'GoogleAdminContactsIcon',
    GoogleAdminContactsIcon
  ),
  GoogleAdminContacts: integrationImageWrapper(
    'GoogleAdminContacts',
    GoogleAdminContacts,
    'svg-grayscale'
  ),

  GoogleClassroomIcon: integrationImageWrapper(
    'GoogleClassroomIcon',
    GoogleClassroomIcon,
    'svg-grayscale'
  ),
  GoogleClassroom: integrationImageWrapper('GoogleClassroom', GoogleClassroom),

  GoogleContactsIcon: integrationImageWrapper('GoogleContactsIcon', GoogleContactsIcon),
  GoogleContacts: integrationImageWrapper('GoogleContacts', GoogleContacts, 'svg-grayscale'),

  GoogleDriveIcon: integrationImageWrapper('GoogleDriveIcon', GoogleDriveIcon),
  GoogleDrive: integrationImageWrapper('GoogleDrive', GoogleDrive),

  GoogleDriveProviderIcon: integrationImageWrapper(
    'GoogleDriveProviderIcon',
    GoogleDriveProviderIcon
  ),

  HellosignIcon: integrationImageWrapper('HellosignIcon', HellosignIcon),
  Hellosign: integrationImageWrapper('Hellosign', Hellosign),

  LocalfilesIcon: integrationImageWrapper('LocalfilesIcon', LocalfilesIcon),
  Localfiles: integrationImageWrapper('Localfiles', Localfiles),

  LocalfilesDarwin: integrationImageWrapper('LocalfilesDarwin', LocalfilesDarwin),

  LocalfilesWin32: integrationImageWrapper('LocalfilesWin32', LocalfilesWin32),

  MailchimpIcon: integrationImageWrapper('MailchimpIcon', MailchimpIcon),
  Mailchimp: integrationImageWrapper('Mailchimp', Mailchimp),

  MicrosoftCalendarIcon: integrationImageWrapper('MicrosoftCalendarIcon', MicrosoftCalendarIcon),
  MicrosoftCalendar: integrationImageWrapper('MicrosoftCalendar', MicrosoftCalendar),

  MiroIcon: integrationImageWrapper("MiroIcon", MiroIcon),
  Miro: integrationImageWrapper("Miro", Miro),

  NoteJoyIcon: integrationImageWrapper('NoteJoyIcon', NoteJoyIcon),
  NoteJoy: integrationImageWrapper('NoteJoy', NoteJoy, 'svg-invert'),

  OneDriveIcon: integrationImageWrapper('OneDriveIcon', OneDriveIcon),
  OneDrive: integrationImageWrapper('OneDrive', OneDrive, 'svg-grayscale'),

  OneNoteIcon: integrationImageWrapper('OneNoteIcon', OneNoteIcon),
  OneNote: integrationImageWrapper('OneNote', OneNote, 'svg-grayscale'),

  OutlookIcon: integrationImageWrapper('OutlookIcon', OutlookIcon),
  Outlook: integrationImageWrapper('Outlook', Outlook, 'svg-grayscale'),

  PcloudIcon: integrationImageWrapper('PcloudIcon', PcloudIcon),
  Pcloud: integrationImageWrapper('Pcloud', Pcloud, 'svg-grayscale'),

  PocketIcon: integrationImageWrapper('PocketIcon', PocketIcon),
  Pocket: integrationImageWrapper('Pocket', Pocket),

  QuipIcon: integrationImageWrapper('QuipIcon', QuipIcon),
  Quip: integrationImageWrapper('Quip', Quip),

  RightsignatureIcon: integrationImageWrapper('RightsignatureIcon', RightsignatureIcon),
  Rightsignature: integrationImageWrapper('Rightsignature', Rightsignature),

  SharepointIcon: integrationImageWrapper('SharepointIcon', SharepointIcon),
  Sharepoint: integrationImageWrapper('Sharepoint', Sharepoint, 'svg-grayscale'),

  SlackIcon: integrationImageWrapper('SlackIcon', SlackIcon),
  Slack: integrationImageWrapper('Slack', Slack),

  SurveyMonkeyIcon: integrationImageWrapper('SurveyMonkeyIcon', SurveyMonkeyIcon),
  SurveyMonkey: integrationImageWrapper('SurveyMonkey', SurveyMonkey),

  TeamsIcon: integrationImageWrapper('TeamsIcon', TeamsIcon),
  Teams: integrationImageWrapper('Teams', Teams, 'svg-grayscale'),

  TrelloIcon: integrationImageWrapper('TrelloIcon', TrelloIcon),
  Trello: integrationImageWrapper('Trello', Trello, 'svg-grayscale'),

  YammerIcon: integrationImageWrapper('YammerIcon', YammerIcon),
  Yammer: integrationImageWrapper('Yammer', Yammer, 'svg-grayscale'),

  ZohoDocsIcon: integrationImageWrapper('ZohoDocsIcon', ZohoDocsIcon),
  ZohoDocs: integrationImageWrapper('ZohoDocs', ZohoDocs, 'svg-grayscale'),

  ZoomIcon: integrationImageWrapper('ZoomIcon', ZoomIcon),
  Zoom: integrationImageWrapper('Zoom', Zoom),
};

export default integrationImages;

export function integrationImageById(
  integrationId,
  IntegrationImages,
  isIcon = false,
  className,
  specialImageType = ''
) {
  const components = {
    airtable_ico: IntegrationImages.AirtableIcon,
    airtable: IntegrationImages.Airtable,

    asana_ico: IntegrationImages.AsanaIcon,
    asana: IntegrationImages.Asana,

    basecamp_ico: IntegrationImages.BasecampIcon,
    basecamp: IntegrationImages.Basecamp,

    box_ico: IntegrationImages.BoxIcon,
    box: IntegrationImages.Box,

    confluence_ico: IntegrationImages.ConfluenceIcon,
    confluence: IntegrationImages.Confluence,

    docusign_ico: IntegrationImages.DocusignIcon,
    docusign: IntegrationImages.Docusign,

    dropbox_ico: IntegrationImages.DropboxIcon,
    dropbox: IntegrationImages.Dropbox,

    dropbox_paper_ico: IntegrationImages.DropboxPaperIcon,
    dropbox_paper: IntegrationImages.DropboxPaper,

    evernote_ico: IntegrationImages.EvernoteIcon,
    evernote: IntegrationImages.Evernote,

    figma_ico: IntegrationImages.FigmaIcon,
    figma: IntegrationImages.Figma,

    folders: IntegrationImages.Folders,

    github_gist_ico: IntegrationImages.GithubGistIcon,
    github_gist: IntegrationImages.GithubGist,

    github_ico: IntegrationImages.GithubIcon,
    github: IntegrationImages.Github,

    gmail_ico: IntegrationImages.GmailIcon,
    gmail: IntegrationImages.Gmail,

    google_admin_contacts_ico: IntegrationImages.GoogleAdminContactsIcon,
    google_admin_contacts: IntegrationImages.GoogleAdminContacts,

    google_classroom_ico: IntegrationImages.GoogleClassroomIcon,
    google_classroom: IntegrationImages.GoogleClassroom,

    google_contacts_ico: IntegrationImages.GoogleContactsIcon,
    google_contacts: IntegrationImages.GoogleContacts,

    google_drive_ico: IntegrationImages.GoogleDriveIcon,
    google_drive: IntegrationImages.GoogleDrive,

    google_drive_provider_ico: IntegrationImages.GoogleDriveProviderIcon,

    hellosign_ico: IntegrationImages.HellosignIcon,
    hellosign: IntegrationImages.Hellosign,

    localfiles_ico: IntegrationImages.LocalfilesIcon,
    localfiles: IntegrationImages.Localfiles,

    localfiles_darwin: IntegrationImages.LocalfilesDarwin,

    localfiles_win32: IntegrationImages.LocalfilesWin32,

    mailchimp_ico: IntegrationImages.MailchimpIcon,
    mailchimp: IntegrationImages.Mailchimp,

    microsoftcalendar_ico: IntegrationImages.MicrosoftCalendarIcon,
    microsoftcalendar: IntegrationImages.MicrosoftCalendar,

    miro_ico: IntegrationImages.MiroIcon,
    miro: IntegrationImages.Miro,

    notejoy_ico: IntegrationImages.NoteJoyIcon,
    notejoy: IntegrationImages.NoteJoy,

    onedrive_ico: IntegrationImages.OneDriveIcon,
    onedrive: IntegrationImages.OneDrive,

    onenote_ico: IntegrationImages.OneNoteIcon,
    onenote: IntegrationImages.OneNote,

    outlook_ico: IntegrationImages.OutlookIcon,
    outlook: IntegrationImages.Outlook,

    pcloud_ico: IntegrationImages.PcloudIcon,
    pcloud: IntegrationImages.Pcloud,

    pocket_ico: IntegrationImages.PocketIcon,
    pocket: IntegrationImages.Pocket,

    quip_ico: IntegrationImages.QuipIcon,
    quip: IntegrationImages.Quip,

    rightsignature_ico: IntegrationImages.RightsignatureIcon,
    rightsignature: IntegrationImages.Rightsignature,

    sharepoint_ico: IntegrationImages.SharepointIcon,
    sharepoint: IntegrationImages.Sharepoint,

    slack_ico: IntegrationImages.SlackIcon,
    slack: IntegrationImages.Slack,

    surveymonkey_ico: IntegrationImages.SurveyMonkeyIcon,
    surveymonkey: IntegrationImages.SurveyMonkey,

    teams_ico: IntegrationImages.TeamsIcon,
    teams: IntegrationImages.Teams,

    trello_ico: IntegrationImages.TrelloIcon,
    trello: IntegrationImages.Trello,

    yammer_ico: IntegrationImages.YammerIcon,
    yammer: IntegrationImages.Yammer,

    zoho_docs_ico: IntegrationImages.ZohoDocsIcon,
    zoho_docs: IntegrationImages.ZohoDocs,

    zoom_ico: IntegrationImages.ZoomIcon,
    zoom: IntegrationImages.Zoom,
  };

  let integrationName = integrationId;

  if (specialImageType && specialImageType === specialIntegrationImageType.provider) {
    integrationName = integrationId + '_provider_ico';
  } else if (specialImageType && specialImageType === specialIntegrationImageType.darwin) {
    integrationName = integrationId + '_darwin';
  } else if (specialImageType && specialImageType === specialIntegrationImageType.win32) {
    integrationName = integrationId + '_win32';
  } else if (isIcon) {
    integrationName = integrationId + '_ico';
  }

  const TagName = components[integrationName];

  if (!TagName) {
    return;
  }

  return <TagName className={className} />;
}
